"use client";

import { useEffect, useRef, useState, useTransition } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import {
  updateAttraction,
  getAvailableGenres,
  getAttractionHidden,
  uploadImage,
  importImageFromUrl,
} from "@/app/admin/actions";
import { GenreTagInput } from "./genre-tag-input";
import { useModalZIndex } from "@/lib/use-modal-z-index";
import { AppQueryProvider } from "@/app/providers";

export interface ArtistEditInput {
  id: number;
  name: string;
  slug: string | null;
  genres: string[];
  spotify_id: string | null;
  image_url: string | null;
  hidden?: boolean;
}

interface ArtistEditModalProps {
  artist: ArtistEditInput;
  onClose: () => void;
  onSave?: (updated: { name: string; image_url: string | null; genres: string[]; spotify_id: string | null }) => void;
}

export function ArtistEditModal(props: ArtistEditModalProps) {
  return <AppQueryProvider><ArtistEditModalContent {...props} /></AppQueryProvider>;
}

function ArtistEditModalContent({ artist, onClose, onSave }: ArtistEditModalProps) {
  const zIndex = useModalZIndex();
  const [isPending, startTransition] = useTransition();
  const [name, setName] = useState(artist.name);
  const [spotifyId, setSpotifyId] = useState(artist.spotify_id ?? "");
  const [imageUrl, setImageUrl] = useState(artist.image_url ?? "");
  const [isUploading, setIsUploading] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [genres, setGenres] = useState<string[]>([...artist.genres]);
  const [hidden, setHidden] = useState(artist.hidden ?? false);
  const [availableGenres, setAvailableGenres] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const router = useRouter();

  useEffect(() => {
    getAvailableGenres().then(setAvailableGenres);
  }, []);

  useEffect(() => {
    if (artist.hidden === undefined) {
      getAttractionHidden(artist.id).then(setHidden);
    }
  }, [artist.hidden, artist.id]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const handleUpload = async (file: File) => {
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("folder", "artists");
      const publicUrl = await uploadImage(formData);
      setImageUrl(publicUrl);
    } catch (err) {
      console.error("Upload failed:", err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleImportUrl = async () => {
    const url = importUrl.trim();
    if (!url) return;
    setIsUploading(true);
    try {
      const publicUrl = await importImageFromUrl(url, "artists");
      setImageUrl(publicUrl);
      setImportUrl("");
    } catch (err) {
      console.error("Import from URL failed:", err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSave = () => {
    if (!name.trim()) return;
    startTransition(async () => {
      await updateAttraction(artist.id, {
        name: name.trim(),
        genres,
        spotify_id: spotifyId.trim() || null,
        image_url: imageUrl.trim() || null,
        hidden,
      });
      queryClient.invalidateQueries({ queryKey: ["admin", "artists"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "events"] });
      router.refresh();
      onSave?.({
        name: name.trim(),
        image_url: imageUrl.trim() || null,
        genres,
        spotify_id: spotifyId.trim() || null,
      });
      onClose();
    });
  };

  return (
    <div
      className="fixed inset-0 flex items-start justify-center overflow-y-auto bg-black/80 px-4 py-12 backdrop-blur-sm"
      style={{ zIndex }}
      onClick={onClose}
    >
      <div
        className="relative flex w-full max-w-md flex-col overflow-visible border border-neutral-800 bg-surface"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Corner markers */}
        <div className="absolute -left-1 -top-1 h-3 w-3 border-l border-t border-acid" />
        <div className="absolute -right-1 -top-1 h-3 w-3 border-r border-t border-acid" />
        <div className="absolute -bottom-1 -left-1 h-3 w-3 border-b border-l border-acid" />
        <div className="absolute -bottom-1 -right-1 h-3 w-3 border-b border-r border-acid" />

        {/* Header */}
        <div className="flex items-center justify-between gap-4 border-b border-neutral-800 p-5">
          <h2 className="font-mono text-sm font-bold tracking-wider text-white">
            EDIT ARTIST
          </h2>
          <button
            onClick={onClose}
            className="shrink-0 text-neutral-500 transition-colors hover:text-white"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M4 4L12 12M12 4L4 12"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="max-h-[70vh] overflow-y-auto p-5 space-y-4">
          <div className="flex items-center gap-2">
            <label className="w-16 shrink-0 font-mono text-[10px] text-neutral-500">
              NAME
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              className="min-w-0 flex-1 rounded border border-neutral-700 bg-dark px-2 py-1 font-mono text-[11px] text-neutral-300 placeholder-neutral-600 outline-none focus:border-acid/50"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="w-16 shrink-0 font-mono text-[10px] text-neutral-500">
              SPOTIFY
            </label>
            <input
              type="text"
              value={spotifyId}
              onChange={(e) => setSpotifyId(e.target.value)}
              placeholder="Spotify ID"
              className="min-w-0 flex-1 rounded border border-neutral-700 bg-dark px-2 py-1 font-mono text-[11px] text-neutral-300 placeholder-neutral-600 outline-none focus:border-green-500/50"
            />
          </div>

          {/* Image upload */}
          <div className="flex items-start gap-2">
            <label className="w-16 shrink-0 pt-1 font-mono text-[10px] text-neutral-500">
              IMAGE
            </label>
            <div className="min-w-0 flex-1">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleUpload(file);
                  e.target.value = "";
                }}
              />
              <div className="group relative aspect-[3/1] w-full overflow-hidden rounded border border-neutral-700 bg-neutral-900">
                {imageUrl ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={imageUrl}
                    alt="Preview"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center">
                    <span className="font-mono text-[10px] text-neutral-600">
                      NO IMAGE
                    </span>
                  </div>
                )}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 transition-opacity group-hover:opacity-100 disabled:cursor-not-allowed"
                >
                  <span className="font-mono text-[10px] font-bold text-white">
                    {isUploading ? "UPLOADING..." : "UPLOAD IMAGE"}
                  </span>
                </button>
              </div>
              <div className="mt-1.5 flex gap-1">
                <input
                  type="text"
                  value={importUrl}
                  onChange={(e) => setImportUrl(e.target.value)}
                  placeholder="Paste image URL to import..."
                  className="min-w-0 flex-1 rounded border border-neutral-700 bg-dark px-2 py-1 font-mono text-[10px] text-neutral-300 placeholder-neutral-600 outline-none focus:border-acid/50"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); handleImportUrl(); }
                  }}
                />
                <button
                  onClick={handleImportUrl}
                  disabled={isUploading || !importUrl.trim()}
                  className="shrink-0 rounded border border-neutral-700 px-2 py-1 font-mono text-[10px] font-bold text-neutral-400 transition-colors hover:text-white disabled:opacity-40"
                >
                  {isUploading ? "..." : "IMPORT"}
                </button>
              </div>
            </div>
          </div>

          <div className="flex items-start gap-2">
            <label className="w-16 shrink-0 pt-1.5 font-mono text-[10px] text-neutral-500">
              GENRES
            </label>
            <div className="min-w-0 flex-1">
              <GenreTagInput
                tags={genres}
                onChange={setGenres}
                options={availableGenres}
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <label className="w-16 shrink-0 font-mono text-[10px] text-neutral-500">
              HIDDEN
            </label>
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={hidden}
                onChange={(e) => setHidden(e.target.checked)}
                className="h-3.5 w-3.5 cursor-pointer appearance-none rounded border border-neutral-700 bg-transparent checked:border-acid checked:bg-acid"
              />
              <span className="font-mono text-[10px] text-neutral-500">
                {hidden ? "Hidden from public" : "Visible"}
              </span>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-2 border-t border-neutral-800 p-5">
          <button
            onClick={onClose}
            className="cursor-pointer rounded border border-neutral-700 px-3 py-2.5 font-mono text-xs font-bold text-neutral-400 transition-colors hover:text-white"
          >
            CANCEL
          </button>
          <button
            onClick={handleSave}
            disabled={isPending || isUploading || !name.trim()}
            className="flex-1 cursor-pointer rounded bg-acid py-2.5 font-mono text-sm font-bold text-black transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {isPending ? "SAVING..." : "SAVE"}
          </button>
        </div>
      </div>
    </div>
  );
}
